import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'

// ── De dónde venías ──────────────────────────────────────────────────────────
//
// El «volver» de una ficha apunta a su lista: desde una factura se vuelve a
// Facturas. Es lo correcto cuando se llegó por la lista, y es lo que hacían las
// cuarenta y siete pantallas que lo llevan.
//
// Deja de serlo en cuanto se llega de otro sitio. Se está trabajando en la ficha
// de un cliente, se abre una de sus facturas desde la pestaña, y «volver» saca
// al listado de facturas: se pierde el cliente, la pestaña en la que se estaba y
// el sitio de la lista. Y no es un caso raro — es la forma NORMAL de moverse por
// un ERP donde todo cuelga de todo.
//
// Así que quien navega deja dicho de dónde viene, y la cabecera de la ficha de
// destino lo prefiere a su vuelta de siempre.
//
// ── Por qué en el estado del historial y no en la dirección ──────────────────
//
// Un `?from=/customers/123` se ve, se copia y se comparte: el enlace que alguien
// manda por WhatsApp llevaría el rastro de la sesión de otro, y el «volver»
// apuntaría a una ficha que quien lo abre a lo mejor ni puede ver.
//
// El estado va atado a la ENTRADA del historial, no a la dirección. Se pierde al
// recargar en algunos navegadores y no viaja en un enlace copiado, y eso es
// exactamente lo que se quiere: cuando no hay rastro, la cabecera vuelve a su
// destino de siempre y nadie se queda sin salida.

/**
 * El título de la pantalla que se está mirando.
 *
 * Lo publica `UiPageHeader`, que es quien lo sabe, y sirve para una sola cosa:
 * poner nombre al «volver» de la ficha siguiente. Por eso el enlace puede decir
 * «Metal·lúrgica Puigcerdà» y no «Volver» — sin que ninguna de las pantallas que
 * navegan tenga que pasar nada.
 */
export const currentPageTitle = ref('')

/**
 * El rastro que deja quien navega, para ponerlo en el `state` del destino.
 *
 * Se usa sobre el `to` de un enlace: `:to="{ ...destino, ...mark }"`. Se calcula
 * al dibujar el enlace, que es cuando todavía se está en la pantalla de origen y
 * por tanto cuando `currentPageTitle` dice lo que tiene que decir.
 */
export function useReturnMark() {
  const route = useRoute()
  return computed(() => ({
    state: { returnTo: { to: route.fullPath, label: currentPageTitle.value } },
  }))
}

/**
 * La regla, sin navegador: qué se hace con el rastro que trae una entrada.
 *
 * Aparte del composable para poder probarla. Lo que decide:
 *
 *  - Sin rastro, o con uno que no tiene forma de rastro, no hay vuelta especial
 *    y manda el `backTo` de la pantalla. Es el caso de quien llegó por la lista,
 *    y también el de un enlace pegado en la barra de direcciones — que es la
 *    razón de comprobar la forma: el `state` del historial lo puede haber
 *    escrito cualquiera.
 *  - Volver a donde ya estás no es volver. Pasa cuando una ficha enlaza a otra
 *    pantalla de sí misma, y dejaría un enlace dando vueltas sobre el sitio.
 */
export function pickReturnTo(state, currentFullPath) {
  const mark = state?.returnTo
  if (!mark || typeof mark.to !== 'string' || !mark.to) return null
  if (mark.to === currentFullPath) return null

  return { to: mark.to, label: typeof mark.label === 'string' ? mark.label : '' }
}

/**
 * De dónde se llegó a esta pantalla, o `null` si nadie lo dijo.
 *
 * Depende de `route.fullPath` a propósito: `history.state` no es reactivo, y sin
 * una dependencia que cambie al navegar esto se quedaría contestando lo de la
 * pantalla anterior.
 */
export function useReturnTo() {
  const route = useRoute()

  return computed(() => {
    // Leído en cada navegación, y sólo para eso.
    void route.fullPath

    return pickReturnTo(typeof history === 'undefined' ? null : history.state, route.fullPath)
  })
}
